import { Navigate, Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { isOmegaAdmin } from '@/features/omega/adminMode';
import { Breadcrumbs } from '@/features/omega/Breadcrumbs';
import { apiFetch } from '@/api/http';
import { fmtBps, proposalStatusColor } from './AlphaOmegaDashboardPage';

// ── Types ────────────────────────────────────────────────────────────────────

interface CompanyResponse {
  company_id: string;
  name: string;
  description: string | null;
  sector: string;
  workers: number;
  royalty_rate_bps: number;
  status: string;
  created_at: string;
}

// ── Sub-components ────────────────────────────────────────────────────────────

function InfoRow({ label, value }: { label: string; value: string | number }) {
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: '160px 1fr', gap: 8,
      padding: '10px 16px',
      borderBottom: '1px solid rgba(255,255,255,0.05)',
    }}>
      <span style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)', textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: 'var(--omega-mono)' }}>{label}</span>
      <span style={{ fontSize: '9pt', color: 'var(--omega-ink)', fontFamily: 'var(--omega-mono)' }}>{value}</span>
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function AlphaOmegaCompanyPage() {
  if (!isOmegaAdmin()) return <Navigate to="/" replace />;

  const { company_id = '' } = useParams<{ company_id: string }>();

  const { data, isPending, isError } = useQuery({
    queryKey: ['alpha-omega', 'company', company_id],
    queryFn: () => apiFetch<CompanyResponse>(`/alpha-omega/portfolio/${company_id}`),
    refetchInterval: 30_000,
    enabled: !!company_id,
  });

  const crumbLabel = data?.name ?? company_id.slice(0, 12);

  return (
    <div className="omega-shell" style={{ fontFamily: 'var(--omega-sans)', color: 'var(--omega-ink)', padding: '16px 20px' }}>
      <Breadcrumbs crumbs={[
        { label: 'OMEGA',        to: '/omega' },
        { label: 'Alpha-Omega',  to: '/omega/alpha-omega' },
        { label: 'Portfolio',          to: '/omega/alpha-omega' },
        { label: crumbLabel },
      ]} />

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, marginTop: 8 }}>
        <h1 style={{ margin: 0, fontSize: '12pt', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          {data?.name ?? 'Loading…'}
        </h1>
        {data && (
          <span style={{
            fontSize: '7.5pt', padding: '2px 8px',
            border: `1px solid ${proposalStatusColor(data.status)}`,
            color: proposalStatusColor(data.status),
            letterSpacing: '0.06em', textTransform: 'uppercase', fontWeight: 700, borderRadius: 3,
          }}>
            {data.status}
          </span>
        )}
        <Link
          to="/omega/alpha-omega"
          style={{
            marginLeft: 'auto', background: 'rgba(125,211,252,0.12)',
            border: '1px solid rgba(125,211,252,0.35)', borderRadius: 4,
            color: '#7dd3fc', fontFamily: 'var(--omega-mono)', fontSize: '8.5pt',
            fontWeight: 600, padding: '5px 12px', textDecoration: 'none',
          }}
        >
          ← Back to portfolio
        </Link>
      </div>

      {isPending && <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '9pt' }}>Loading…</div>}
      {isError   && <div style={{ color: '#f87171', fontSize: '9pt' }}>Error loading company.</div>}

      {data && (
        <>
          <div style={{
            background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: 6, overflow: 'hidden', maxWidth: 560, marginBottom: 24,
          }}>
            <InfoRow label="ID"         value={data.company_id} />
            <InfoRow label="Sector"          value={data.sector} />
            <InfoRow label="Workers"       value={data.workers} />
            <InfoRow label="Royalty (bps)"  value={fmtBps(data.royalty_rate_bps)} />
            <InfoRow label="Created"         value={data.created_at} />
            {data.description && <InfoRow label="Description" value={data.description} />}
          </div>

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <Link to={`/omega/alpha-omega/spawn-proposals`} style={linkStyle('#7dd3fc')}>Proposals spawn</Link>
            <Link to={`/omega/alpha-omega/monthly-pl`}      style={linkStyle('#a78bfa')}>Monthly CPA</Link>
            <Link to={`/omega/alpha-omega/royalty-tracking`} style={linkStyle('#34d399')}>Royalties</Link>
            <Link to={`/omega/alpha-omega/worker-allocation`} style={linkStyle('#fbbf24')}>Workers</Link>
          </div>
        </>
      )}
    </div>
  );
}

function linkStyle(color: string): React.CSSProperties {
  return {
    background: `rgba(${hexToRgb(color)},0.10)`,
    border: `1px solid rgba(${hexToRgb(color)},0.35)`,
    borderRadius: 4, color, fontFamily: 'var(--omega-mono)',
    fontSize: '8.5pt', fontWeight: 600, padding: '6px 14px',
    textDecoration: 'none', letterSpacing: '0.04em',
  };
}

function hexToRgb(hex: string): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `${r},${g},${b}`;
}
